import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Clock, Download, Eye, File as FileIcon, FileImage, FileSpreadsheet, FileText, FolderOpen, Lock, ShieldCheck } from "lucide-react";
import "./ShareLanding.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  pdf: { cls: "pdf", label: "PDF" },
  dwg: { cls: "dwg", label: "DWG" },
  dxf: { cls: "dwg", label: "DXF" },
  xls: { cls: "xls", label: "XLS" },
  xlsx: { cls: "xls", label: "XLSX" },
  csv: { cls: "xls", label: "CSV" },
  png: { cls: "img", label: "IMG" },
  jpg: { cls: "img", label: "IMG" },
  jpeg: { cls: "img", label: "IMG" },
  gif: { cls: "img", label: "IMG" },
  webp: { cls: "img", label: "IMG" },
  zip: { cls: "zip", label: "ZIP" },
  rar: { cls: "zip", label: "RAR" },
};
const PREVIEWABLE = new Set(["pdf", "png", "jpg", "jpeg", "gif", "webp", "svg", "txt"]);
const HEROES = ["/hero-1.jpg", "/hero-2.jpg", "/hero-3.jpg", "/hero-4.jpg"];

const extOf = (name) => (name || "").split(".").pop().toLowerCase();
const typeOf = (name) => TYPE_STYLES[extOf(name)] || { cls: "gen", label: extOf(name).slice(0, 4).toUpperCase() || "FILE" };

const fmtSize = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

const ERROR_STATES = {
  expired: {
    title: "This link has expired",
    body: "The share link you opened reached its expiration date. Please contact the sender if you still need access to these files.",
  },
  revoked: {
    title: "This link is no longer available",
    body: "The sender has revoked this share link. Please contact them if you believe this is a mistake.",
  },
  invalid: {
    title: "This link isn't valid",
    body: "The share link may be incorrect or incomplete. Please check the URL you received and try again.",
  },
};

function TypeIcon({ name }) {
  const t = typeOf(name);
  const Icon = t.cls === "img" ? FileImage : t.cls === "xls" ? FileSpreadsheet : t.cls === "pdf" ? FileText : FileIcon;
  return (
    <span className={`sl-type sl-type-${t.cls}`}>
      <Icon className="sl-type-icon" />
      <span className="sl-type-label">{t.label}</span>
    </span>
  );
}

export default function ShareLanding() {
  const { token } = useParams();
  const heroIdx = Math.abs([...(token || "")].reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0)) % HEROES.length;
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    api
      .get(`/public/share/${token}`)
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) => {
        const d = e?.response?.data?.detail;
        const reason = typeof d === "object" && d ? d.reason : null;
        setState({ loading: false, error: reason || (e?.response?.status === 404 ? "invalid" : "invalid"), data: null });
      });
  }, [token]);

  const { loading, error, data } = state;
  const daysLeft = data ? Math.max(0, Math.ceil((new Date(data.expires_at) - Date.now()) / 86400000)) : 0;
  const canDownload = data?.permission === "view_download";
  const isFolder = data?.target_type === "folder";
  const folderGroups = data
    ? Object.entries(
        data.files.reduce((m, f) => {
          const k = f.folder || "";
          (m[k] = m[k] || []).push(f);
          return m;
        }, {})
      ).sort((a, b) => a[0].localeCompare(b[0]))
    : [];
  const showGroupHeaders = isFolder && folderGroups.length > 1;
  const slugify = (s) => (s || "root").replace(/[^a-z0-9]+/gi, "-");
  const senderInitials = (data?.sender_name || "P").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="sl-page" style={{ backgroundImage: `url(${HEROES[heroIdx]})` }}>
      <div className="sl-veil" />
      <div className="sl-wrap">
        <header className="sl-top" data-testid="share-header">
          <img src="/pruva-logo-full.png" alt="Pruva Swimming Pools" className="logo-icon sl-top-logo" />
          <span className="sl-drive-pill">Drive</span>
          <span className="sl-tagline">Engineering the extraordinary in acrylic &amp; water</span>
        </header>

        {loading && (
          <div className="sl-card sl-center" data-testid="share-loading">
            <span className="sl-spinner" />
            <p>Loading shared files…</p>
          </div>
        )}

        {!loading && error && (
          <div className="sl-card sl-center" data-testid="share-error">
            <span className="sl-error-icon"><Lock size={26} /></span>
            <h1 className="sl-error-title" data-testid="share-error-title">{ERROR_STATES[error]?.title || ERROR_STATES.invalid.title}</h1>
            <p className="sl-error-body">{ERROR_STATES[error]?.body || ERROR_STATES.invalid.body}</p>
            <p className="sl-footer">Shared via Pruva Drive · pruvapools.com</p>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="sl-sender" data-testid="share-sender">
              <span className="sl-avatar">{senderInitials}</span>
              <p><b>{data.sender_name}</b> shared {isFolder ? "a folder" : "a file"} with you via Pruva Drive</p>
            </div>

            <main className="sl-card" data-testid="share-card">
              <div className="sl-card-head">
                <span className="sl-target-icon">{isFolder ? <FolderOpen size={24} /> : <TypeIcon name={data.label} />}</span>
                <div className="sl-target-meta">
                  <h1 className="sl-title" data-testid="share-title">{data.label}</h1>
                  <p className="sl-meta">
                    {isFolder ? `${data.files.length} file${data.files.length === 1 ? "" : "s"} · ` : ""}{fmtSize(data.total_size)} · Shared {fmtDate(data.created_at)}
                  </p>
                </div>
              </div>

              <div className="sl-badges">
                <span className={`sl-badge ${canDownload ? "sl-badge-green" : "sl-badge-amber"}`} data-testid="share-permission-badge">
                  {canDownload ? <Download size={12} /> : <Eye size={12} />}
                  {canDownload ? "View & download" : "View only"}
                </span>
                <span className="sl-badge sl-badge-blue" data-testid="share-expiry-badge">
                  <Clock size={12} /> Expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}
                </span>
                <span className="sl-badge sl-badge-slate" data-testid="share-nosignin-badge">
                  <ShieldCheck size={12} /> No sign-in required
                </span>
              </div>

              <ul className="sl-files" data-testid="share-file-list">
                {data.files.length === 0 && <li className="sl-empty">This folder is empty.</li>}
                {folderGroups.map(([gfolder, gfiles]) => (
                  <Fragment key={gfolder || "root"}>
                    {showGroupHeaders && (
                      <li className="sl-file" style={{ cursor: "default" }} data-testid={`share-group-${slugify(gfolder)}`}>
                        <span className="sl-file-name" style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.55 }}>{gfolder || data.label}</span>
                      </li>
                    )}
                    {gfiles.map((f) => {
                      const ext = extOf(f.filename);
                      const previewable = PREVIEWABLE.has(ext);
                      return (
                        <li key={f.id} className="sl-file" data-testid={`share-file-${f.id}`}>
                          <TypeIcon name={f.filename} />
                          <span className="sl-file-name" title={f.filename}>{f.filename}</span>
                          <span className="sl-file-size">{fmtSize(f.size)}</span>
                          <span className="sl-file-actions">
                            {previewable && (
                              <a className="sl-icon-btn" href={`${API}/public/share/${token}/files/${f.id}/preview`} target="_blank" rel="noreferrer" title="Preview in browser" data-testid={`share-preview-${f.id}`}>
                                <Eye size={15} />
                              </a>
                            )}
                            {canDownload && (
                              <a className="sl-icon-btn sl-icon-btn-accent" href={`${API}/public/share/${token}/files/${f.id}/download`} title="Download" data-testid={`share-download-${f.id}`}>
                                <Download size={15} />
                              </a>
                            )}
                            {!canDownload && !previewable && <span className="sl-file-note">Download not enabled</span>}
                          </span>
                        </li>
                      );
                    })}
                  </Fragment>
                ))}
              </ul>

              <div className="sl-actions">
                <div className="sl-action-btns">
                  {isFolder && canDownload && data.files.length > 1 && (
                    <a className="sl-btn-primary" href={`${API}/public/share/${token}/zip`} data-testid="share-download-zip">
                      <Download size={16} /> Download all as .zip
                    </a>
                  )}
                  {!isFolder && data.files.length === 1 && PREVIEWABLE.has(extOf(data.files[0].filename)) && (
                    <a className="sl-btn-ghost" href={`${API}/public/share/${token}/files/${data.files[0].id}/preview`} target="_blank" rel="noreferrer" data-testid="share-preview-main">
                      <Eye size={16} /> Preview in browser
                    </a>
                  )}
                </div>
                <span className="sl-count">{data.files.length} item{data.files.length === 1 ? "" : "s"} · {fmtSize(data.total_size)}</span>
              </div>
            </main>

            <div className="sl-notice" data-testid="share-security-notice">
              <ShieldCheck size={18} className="sl-notice-icon" />
              <p>This link was shared directly by a Pruva team member. Anyone with the URL can open it — no account or sign-in is needed. If you weren't expecting this link, please contact the sender before opening any files.</p>
            </div>

            <p className="sl-footer">Shared via Pruva Drive · pruvapools.com</p>
          </>
        )}
      </div>
    </div>
  );
}
